import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

// Routing gates for Otto-only accounts. CRITICAL: the account row loads a
// beat after the profile in AuthContext — while it's in flight we must show
// a spinner, never default to "full account", or Otto-only users flash
// into /dashboard (and vice versa).

function useAccountGate() {
  const { profile, account, isLoading } = useAuth()
  const accountPending = !!profile?.account_id && !account
  return {
    pending: isLoading || accountPending,
    ottoOnly: account?.account_type === 'miles_only',
  }
}

function GateSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )
}

/** Wraps the full BR dashboard: Otto-only accounts are sent to /otto. */
export function RequireFullAccount({ children }: { children: ReactNode }) {
  const { pending, ottoOnly } = useAccountGate()
  if (pending) return <GateSpinner />
  if (ottoOnly) return <Navigate to="/otto" replace />
  return <>{children}</>
}

/** Wraps the Otto portal: full accounts are sent to /dashboard. */
export function RequireOttoOnly({ children }: { children: ReactNode }) {
  const { pending, ottoOnly } = useAccountGate()
  if (pending) return <GateSpinner />
  if (!ottoOnly) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}
